import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import * as routes from 'constants/routes';
import { QRCodeSVG } from 'qrcode.react';
import copyIconImg from '../../../assets/Icon/Copyicon.svg';
import BaseWrapper from '../../../components/layout/BaseWrapper';
import {
  ProgressBar,
  TimerDisplay,
  NetworkChip,
  FlowTag,
} from '../../../components/WalletEntry';
import { Belt } from '../../../components/WalletEntry/styled';
import { useWalletAddressTimer } from '../../../hooks/useWalletAddressTimer';
import { useUser } from '../../../api/userService';
import { initializeSocket, socket } from '../../../api/socketService';
import { usePaymentSession, clearPaymentSession } from './hooks/usePaymentSession';
import { usePaymentPolling } from './hooks/usePaymentPolling';
import { useStakeSessionPolling } from './hooks/useStakeSessionPolling';
import { publishTournament, createStakePaymentSessionWithRetry } from '../../../api/paymentService';
import { joinTournament } from '../../../api/tournamentService';
import PaymentInstructions from '../../../components/Payment/PaymentInstructions';
import PaymentStatusDisplay from '../../../components/Payment/PaymentStatusDisplay';
import { sanitizeProviderData } from '../../../components/TournamentJoinPayment/sanitizeProviderData';
import type { PaymentStatus, Payment, PaymentType, StakePaymentSession, PaymentSessionUpdatedPayload } from '../../../types/payment';
import { buildSolanaPayUrl } from '../../../utils/solanaPay';
import {
  trackPaymentConfirmed,
  trackTournamentPublished,
  trackTournamentJoined,
  trackPaymentFailed,
  trackPaymentExpired,
} from '../../../utils/analytics/paymentEvents';
import { getTranslations, interpolate } from './i18n';
import {
  ScreenContainer,
  ScreenTitle,
  ContentWrapper,
  LoadingSkeleton,
  SkeletonLine,
  SkeletonQR,
  StatusContainer,
  ErrorBanner,
  // Figma-aligned UI
  ProgressSteps,
  ProgressLine,
  ProgressStep,
  ProgressDot,
  ProgressLabel,
  TimerBar,
  TimerLabel,
  TimerValue,
  PaymentTitle,
  PaymentSubtitle,
  NetworkInfo,
  ActionButtons,
  PrimaryButton,
  SecondaryButton,
  // New layout components
  GradientSection,
  HeaderStack,
  Content,
  QRContainer,
  QRFrame,
  QRArrowIn,
  AddressSection,
  AddressLabel,
  AddressValue,
  AddressRow,
  AddressMeta,
  AddressActions,
  RevealButton,
  VerificationContainer,
  VerificationTitle,
  VerificationStatusText,
  VerificationSubText,
  Spinner,
  VerificationHero,
  VerifyOrb,
  VerifyOrbCore,
  VerifyStatus,
  VerifyPulseDot,
  VerifyAmountSkeleton,
  DetectedContinueWrapper,
  DetectedContinueButton,
  BackToPaymentButton,
  SuccessIcon,
  UnderpaidTitle,
  UnderpaidDetails,
  UnderpaidRow,
  UnderpaidLabel,
  UnderpaidValue,
  UnderpaidRemaining,
  UnderpaidHint,
  CopyButton,
  OverpaymentDivider,
  OverpaymentMessage,
  OverpaymentRow,
  OverpaymentLabel,
  OverpaymentValue,
  OverpaymentAmountLabel,
  OverpaymentAmountValue,
  OverpaymentHint,
} from './styled';

/**
 * Mid-ellipsis truncation for blockchain addresses — keeps the first 8 and
 * last 8 characters so the user can still verify the start/end of the
 * address without seeing the full string. Reveal/Hide toggle restores it.
 */
const truncateAddress = (s: string, head = 8, tail = 8): string => {
  if (!s || s.length <= head + tail + 1) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
};

/**
 * User-facing label for a blockchain network identifier sent by the backend.
 * Mainnet collapses to the bare chain name (e.g. 'solana-mainnet-beta' →
 * 'SOLANA') because users don't need to see the operational suffix on
 * production. Testnet keeps its name so QA / dev / staging can tell at a
 * glance which environment they're paying on.
 *
 * Falls back to uppercased input with separators normalised, so any future
 * network identifier still renders sanely without a code change.
 */
const formatNetworkLabel = (raw: string | undefined | null): string => {
  if (!raw) return '';
  const normalised = raw.toLowerCase().trim();

  // Solana
  if (normalised === 'solana' || normalised === 'solana-mainnet' || normalised === 'solana-mainnet-beta') {
    return 'SOLANA';
  }
  if (normalised === 'solana-devnet' || normalised === 'devnet') {
    return 'SOLANA DEVNET';
  }
  if (normalised === 'solana-testnet' || normalised === 'testnet') {
    return 'SOLANA TESTNET';
  }

  // Polygon
  if (normalised === 'polygon' || normalised === 'polygon-mainnet' || normalised === 'matic') {
    return 'POLYGON';
  }
  if (normalised === 'polygon-amoy' || normalised === 'amoy' || normalised === 'maticamoy') {
    return 'POLYGON AMOY';
  }
  if (normalised === 'polygon-mumbai' || normalised === 'mumbai') {
    return 'POLYGON MUMBAI';
  }

  // Unknown → best-effort uppercase
  return normalised.replace(/[-_]/g, ' ').toUpperCase();
};

/**
 * Best-effort fallback ticker derived from a backend `network` string.
 * Used when `payment.currency` is empty (some legacy verify-state
 * responses) but providerData.network is populated. Returning '' lets
 * the caller decide whether to render a skeleton instead.
 */
const deriveTickerFromNetwork = (raw: string | undefined | null): string => {
  if (!raw) return '';
  const n = raw.toLowerCase().trim();
  if (n.includes('solana') || n === 'devnet' || n === 'testnet') return 'SOL';
  if (n.includes('polygon') || n === 'matic' || n === 'maticamoy' || n === 'amoy' || n === 'mumbai') return 'POL';
  return '';
};

/**
 * Check glyph for the SuccessIcon badge on the Detected/equal+over
 * variants. Inline SVG keeps it consistent with the bull-on-bull DS-2
 * spec (single solid stroke, no asset round-trip).
 */
const CheckGlyph: React.FC<{ size?: number }> = ({ size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 12.5l4 4 10-10"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Shield glyph for the VerifyOrb core. Inline SVG keeps it tree-shake
 * friendly and avoids another asset round-trip on a screen the user
 * stares at while a transaction confirms.
 */
const ShieldGlyph: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3l8 3v5.5c0 4.7-3.4 9-8 10-4.6-1-8-5.3-8-10V6l8-3z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
      fill="rgba(82, 211, 129, 0.10)"
    />
    <path
      d="M8.5 12.2l2.4 2.4 4.6-4.8"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * PaymentQR Screen Component
 * Task 9.1-9.4: Full payment screen with routing, auth, session, polling, and conditional UI
 *
 * Route: /payment-qr/:id
 * Displays payment instructions with QR code for tournament creation payments.
 */

export interface PaymentQRProps {
  /** Optional: Override paymentId from URL (for testing) */
  paymentId?: string;
  /** Optional: Callback on successful publish */
  onPublishSuccess?: (tournamentId: string) => void;
  /** Optional: Callback on critical error */
  onCriticalError?: (error: Error) => void;
}

/**
 * Check if status is terminal (no more polling needed)
 */
const isTerminalStatus = (status: PaymentStatus): boolean =>
  ['CONFIRMED', 'FAILED', 'EXPIRED', 'REFUNDED', 'CHARGEBACKED', 'USED'].includes(status);

/**
 * Render the payment title with the `{amount} {currency}` fragment highlighted
 * using a <span>, so it can inherit the primary color from PaymentTitle styles.
 *
 * This works for both PL and EN, and for any template that uses
 * `{amount}` and `{currency}` placeholders in any order.
 */
const renderAmountTitle = (
  template: string,
  amount: string,
  currency: string,
) => {
  const full = interpolate(template, { amount, currency });
  const trimmedAmount = amount.trim();
  const trimmedCurrency = currency.trim();
  const target = [trimmedAmount, trimmedCurrency].filter(Boolean).join(' ');

  if (!target) {
    return full;
  }

  const idx = full.indexOf(target);

  // Fallback: if we can't find the substring, return plain text
  if (idx === -1) {
    return full;
  }

  const before = full.slice(0, idx);
  const after = full.slice(idx + target.length);

  return (
    <>
      {before}
      <span>{target}</span>
      {after}
    </>
  );
};

/**
 * Splits the localized "Verifying transaction on {network}…" template at
 * `{network}` and wraps the inserted label in a bull-green emphasis span,
 * matching the `span.network` selector inside `VerifyStatus`.
 */
const renderTransactionOnNetwork = (template: string, networkLabel: string) => {
  const [before, after = ''] = template.split('{network}');
  return (
    <>
      {before}
      <span className="network">{networkLabel}</span>
      {after}
    </>
  );
};

const PaymentQR: React.FC<PaymentQRProps> = ({
  paymentId: propPaymentId,
  onPublishSuccess,
  onCriticalError,
}) => {
  const { id: urlPaymentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as { from?: string; tournamentId?: string; hash?: string } | null;
  const { isLoggedIn } = useUser();

  const searchParams = new URLSearchParams(location.search);
  const mode = searchParams.get('mode');

  // Compute paymentId and isJoinFlow before hooks (no early returns before hooks)
  const paymentId = propPaymentId || urlPaymentId || '';
  const isJoinFlow = locationState?.from === 'tournament-join';

  // Auth gating - redirect to home if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/', { replace: true });
    }
  }, [isLoggedIn, navigate]);

  // Ownership error handler
  const handleOwnershipError = useCallback(() => {
    console.error('Payment ownership mismatch - redirecting');
    navigate('/creating-tournament', { replace: true });
    onCriticalError?.(new Error('Payment ownership mismatch'));
  }, [navigate, onCriticalError]);

  // Session hydration (disabled when in wallet-assignment mode or not logged in)
  const isLegacyPaymentMode = isLoggedIn && mode !== 'wallet-assignment';
  const {
    payment: sessionPayment,
    tournamentId,
    isLoading,
    error: sessionError,
    isOwnershipValid,
    refetch,
  } = usePaymentSession({
    // wallet-assignment mode: pass empty paymentId to disable usePaymentSession
    // (the hook has an early-return guard for empty paymentId)
    paymentId: isLegacyPaymentMode ? paymentId : '',
    onOwnershipError: handleOwnershipError,
  });

  // Status polling (disabled when in wallet-assignment mode or not logged in)
  const {
    currentStatus,
    payment: pollingPayment,
    isPolling,
    error: pollingError,
    startPolling,
  } = usePaymentPolling({
    paymentId: isLegacyPaymentMode ? paymentId : '',
    initialStatus: sessionPayment?.status || 'PENDING',
    enabled: isLegacyPaymentMode && !isLoading && !!sessionPayment && !isTerminalStatus(sessionPayment.status),
  });

  // Use polling payment if available, otherwise session payment
  const payment = pollingPayment || sessionPayment;
  const status = currentStatus || sessionPayment?.status || 'PENDING';

  // Handle cancel action
  const handleCancel = useCallback(() => {
    navigate('/creating-tournament', { replace: true });
  }, [navigate]);

  // Handle retry action (for FAILED status)
  const handleRetry = useCallback(() => {
    navigate(`/creating-tournament/${tournamentId}`, { replace: true });
  }, [navigate, tournamentId]);

  // Handle create new action (for EXPIRED status)
  const handleCreateNew = useCallback(() => {
    clearPaymentSession(paymentId);
    navigate(`/creating-tournament/${tournamentId}`, { replace: true });
  }, [navigate, tournamentId, paymentId]);

  // Handle back action
  const handleBack = useCallback(() => {
    navigate('/', { replace: true });
  }, [navigate]);

  // Handle contact support
  const handleContact = useCallback(() => {
    window.open('mailto:support@tradingbattles.com', '_blank');
  }, []);

  // Handle manual confirm action (forces immediate refresh/poll)
  const handleConfirmPayment = useCallback(() => {
    void refetch();
    startPolling?.();
  }, [refetch, startPolling]);

  // Publish state
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState<Error | null>(null);
  const publishAttemptedRef = useRef(false);

  // Countdown timer to expiry (Figma: TimerBar)
  const [timeLeft, setTimeLeft] = useState<string | null>(null);
  useEffect(() => {
    if (!payment?.expiresAt) {
      setTimeLeft(null);
      return;
    }

    const update = () => {
      const expiresAtMs = new Date(payment.expiresAt as string).getTime();
      const now = Date.now();
      const diff = Math.max(0, Math.floor((expiresAtMs - now) / 1000));
      const mm = Math.floor(diff / 60)
        .toString()
        .padStart(1, '0');
      const ss = Math.floor(diff % 60)
        .toString()
        .padStart(2, '0');
      setTimeLeft(`${mm}:${ss}`);
    };

    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [payment?.expiresAt]);

  // Handle CONFIRMED status - publish tournament OR join tournament
  useEffect(() => {
    const handleConfirmed = async () => {
      // Only process once and only for CONFIRMED status
      if (status !== 'CONFIRMED' || publishAttemptedRef.current || !tournamentId) {
        return;
      }

      publishAttemptedRef.current = true;
      setIsPublishing(true);
      setPublishError(null);

      // Fire GTM event for payment confirmed
      trackPaymentConfirmed({
        paymentId,
        tournamentId,
        amount: payment?.amount,
        currency: payment?.currency,
      });

      try {
        if (isJoinFlow) {
          // Join flow: call joinTournament with hash and paymentId
          const hash = locationState?.hash;
          await joinTournament(tournamentId, hash, paymentId);

          // Fire analytics: tournament joined (non-blocking)
          try {
            trackTournamentJoined({
              tournamentId,
              paymentId,
              amount: payment?.amount,
              currency: payment?.currency,
              status: 200,
            });
          } catch (analyticsErr) {
            console.error('Analytics error (non-blocking):', analyticsErr);
          }

          // Clean up localStorage
          clearPaymentSession(paymentId);

          // Redirect to tournament page
          navigate(`/tournament/${tournamentId}`);
        } else {
          // Create flow: call publishTournament
          const result = await publishTournament(tournamentId, paymentId);

          // Fire GTM event for tournament published
          trackTournamentPublished({
            tournamentId: result.id,
            paymentId,
          });

          // Clean up localStorage
          clearPaymentSession(paymentId);

          // Notify parent
          onPublishSuccess?.(result.id);

          // Redirect to tournament lobby/share screen
          const sharePath = routes.TOURNAMENT_SHARE.replace(':id', result.id);
          navigate(sharePath);
        }
      } catch (error) {
        // For join flow, treat 409 ALREADY_JOINED as success
        if (isJoinFlow) {
          const axiosError = error as { response?: { status?: number; data?: { code?: string } } };
          if (axiosError.response?.status === 409 && axiosError.response?.data?.code === 'ALREADY_JOINED') {
            // Fire analytics: tournament joined with 409 (non-blocking)
            try {
              trackTournamentJoined({
                tournamentId,
                paymentId,
                amount: payment?.amount,
                currency: payment?.currency,
                status: 409,
                alreadyJoined: true,
              });
            } catch (analyticsErr) {
              console.error('Analytics error (non-blocking):', analyticsErr);
            }

            // Already joined - treat as success
            clearPaymentSession(paymentId);
            navigate(`/tournament/${tournamentId}`);
            return;
          }
        }

        const publishErr = error instanceof Error ? error : new Error('Failed to complete action');
        setPublishError(publishErr);
        setIsPublishing(false);
      }
    };

    handleConfirmed();
  }, [status, tournamentId, paymentId, payment, navigate, onPublishSuccess, isJoinFlow]);

  // Track FAILED/EXPIRED status for analytics
  const trackedTerminalRef = useRef<PaymentStatus | null>(null);
  useEffect(() => {
    // Only track once per terminal status
    if (!payment || trackedTerminalRef.current === status) return;

    if (status === 'FAILED') {
      trackedTerminalRef.current = status;
      trackPaymentFailed({
        paymentId,
        tournamentId,
        amount: payment.amount,
        currency: payment.currency,
        errorCode: pollingError?.message,
      });
    } else if (status === 'EXPIRED') {
      trackedTerminalRef.current = status;
      trackPaymentExpired({
        paymentId,
        tournamentId,
        amount: payment.amount,
        currency: payment.currency,
      });
    }
  }, [status, payment, paymentId, tournamentId, pollingError]);

  // Don't render if not authenticated
  if (!isLoggedIn) {
    return null;
  }

  // Wallet-assignment mode: stake payment via unified payment-session (see WalletAssignmentQR)
  if (mode === 'wallet-assignment') {
    return (
      <WalletAssignmentQR
        tournamentId={locationState?.tournamentId || urlPaymentId || ''}
        hash={locationState?.hash}
        isJoinFlow={locationState?.from === 'tournament-join'}
      />
    );
  }

  const t = getTranslations('en');

  // Determine what to render based on status
  const shouldShowInstructions = status === 'PENDING' && payment;
  const shouldShowStatusDisplay = isTerminalStatus(status) && payment && !isPublishing;
  const shouldShowPublishLoading = status === 'CONFIRMED' && isPublishing;
  const shouldShowPublishError = status === 'CONFIRMED' && publishError;

  return (
    <ScreenContainer
      role="main"
      aria-labelledby="payment-qr-title"
      data-testid="payment-qr-screen"
    >
      <ScreenTitle id="payment-qr-title">
        {t.screen.title}
      </ScreenTitle>

      <ContentWrapper>
        {/* Figma: Progress steps + timer + titles for PENDING */}
        {shouldShowInstructions && (
          <>
            <ProgressSteps>
              <ProgressLine />
              <ProgressStep $active>
                <ProgressDot $active />
                <ProgressLabel $active>{t.progress.payment}</ProgressLabel>
              </ProgressStep>
              <ProgressStep>
                <ProgressDot />
                <ProgressLabel>{t.progress.verification}</ProgressLabel>
              </ProgressStep>
              <ProgressStep>
                <ProgressDot />
                <ProgressLabel>{t.progress.game}</ProgressLabel>
              </ProgressStep>
            </ProgressSteps>

            <TimerBar aria-live="polite">
              <TimerLabel>{t.timer.label}</TimerLabel>
              <TimerValue
                $isWarning={(() => {
                  if (!payment?.expiresAt || !timeLeft) return false;
                  const [m] = timeLeft.split(':');
                  return Number(m) <= 2;
                })()}
                $isCritical={(() => {
                  if (!payment?.expiresAt || !timeLeft) return false;
                  const [m, s] = timeLeft.split(':');
                  return Number(m) === 0 && Number(s) <= 30;
                })()}
              >
                {timeLeft ?? '--:--'}
              </TimerValue>
            </TimerBar>

            <PaymentTitle>
              {renderAmountTitle(
                isJoinFlow ? t.instructions.titleJoin : t.instructions.title,
                payment?.amount || '',
                payment?.currency || '',
              )}
            </PaymentTitle>
            <NetworkInfo>
              {t.instructions.description.replace('POLYGON (MATIC)',
                `${payment?.providerData?.network?.toUpperCase() ?? ''}${payment?.providerData?.tokenSymbol ? ` (${payment.providerData.tokenSymbol})` : ''}`
              )}
            </NetworkInfo>
          </>
        )}
        {/* Loading skeleton */}
        {isLoading && (
          <LoadingSkeleton data-testid="payment-qr-loading">
            <SkeletonLine $width="60%" $height="28px" />
            <SkeletonLine $width="80%" />
            <SkeletonLine $width="40%" />
            <SkeletonQR />
            <SkeletonLine $width="100%" />
            <SkeletonLine $width="70%" />
          </LoadingSkeleton>
        )}

        {/* Publish loading state */}
        {shouldShowPublishLoading && (
          <LoadingSkeleton data-testid="publish-loading">
            <SkeletonLine $width="60%" $height="28px" />
            <SkeletonLine $width="80%" />
            <p>{isJoinFlow ? t.loading.joining : t.loading.publishing}</p>
          </LoadingSkeleton>
        )}

        {/* Publish error state */}
        {shouldShowPublishError && (
          <ErrorBanner data-testid="publish-error" role="alert">
            <span>⚠️</span>
            <span>{interpolate(isJoinFlow ? t.errors.joinFailed : t.errors.publishFailed, { message: publishError?.message || '' })}</span>
          </ErrorBanner>
        )}

        {/* Payment Instructions for PENDING */}
        {!isLoading && shouldShowInstructions && payment && (
          <StatusContainer
            $status={status}
            role="status"
            aria-live="polite"
            aria-busy={isPolling}
            data-testid="payment-instructions"
          >
            <PaymentInstructions
              payment={{
                id: payment.id,
                status: payment.status,
                type: payment.type,
                tournamentId: payment.tournamentId,
                amount: payment.amount,
                currency: payment.currency,
                provider: payment.provider,
                expiresAt: payment.expiresAt,
                // Task 30.1: Sanitize providerData to prevent XSS attacks
                // Type assertion needed because sanitizeProviderData returns a more permissive type
                providerData: sanitizeProviderData(payment.providerData as Parameters<typeof sanitizeProviderData>[0]) as typeof payment.providerData,
              }}
              onCancel={handleCancel}
              hideCancel
            />
          </StatusContainer>
        )}

        {/* Figma: Action buttons below instructions (Cancel / Confirm) */}
        {shouldShowInstructions && (
          <ActionButtons>
            <SecondaryButton type="button" onClick={handleCancel}>{t.actions.cancel}</SecondaryButton>
            <PrimaryButton type="button" onClick={handleConfirmPayment}>{t.actions.confirmPayment}</PrimaryButton>
          </ActionButtons>
        )}

        {/* Payment Status Display for terminal states */}
        {!isLoading && shouldShowStatusDisplay && (
          <StatusContainer
            $status={status}
            role="status"
            aria-live="polite"
            aria-busy={false}
            data-testid="payment-status-container"
          >
            <PaymentStatusDisplay
              status={status}
              amount={payment?.amount}
              currency={payment?.currency}
              expiresAt={payment?.expiresAt}
              onRetry={status === 'FAILED' ? handleRetry : undefined}
              onCreateNew={status === 'EXPIRED' ? handleCreateNew : undefined}
              onBack={handleBack}
              onContact={handleContact}
            />
          </StatusContainer>
        )}
      </ContentWrapper>
    </ScreenContainer>
  );
};

/**
 * WalletAssignmentQR - Stake payment screen (owner + joiner)
 *
 * Uses unified payment-session as source of truth for payment status.
 * Handles both flows via `isJoinFlow` prop.
 * Flow:
 * 1. Create stake payment-session via POST /tournaments/:id/payment-sessions/join
 * 2. Listen to paymentSessionUpdated for status changes
 * 3. On CONFIRMED_EXACT/OVERPAID: auto-join + show success + redirect
 * 4. On TIMEOUT: show error + retry CTA
 */
const WalletAssignmentQR: React.FC<{ tournamentId: string; hash?: string; isJoinFlow?: boolean }> = ({ tournamentId, hash, isJoinFlow = false }) => {
  const navigate = useNavigate();
  const { jwt: token } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [stakeSession, setStakeSession] = useState<StakePaymentSession | null>(null);
  const [copied, setCopied] = useState(false);
  const [addressRevealed, setAddressRevealed] = useState(false);
  const [step, setStep] = useState<'payment' | 'verification' | 'game'>('payment');
  const [verificationMode, setVerificationMode] = useState<'generic' | 'underpaid' | 'success' | 'timeout'>('generic');
  // Single source of truth for "the user explicitly chose this view".
  // Polling and socket updates are advisory: when the user has
  // committed to a view via Confirm-payment or Back-to-payment, an
  // intermediate polling tick that still carries the old status must
  // not yank them somewhere else. Cleared whenever the session hits
  // a terminal status (CONFIRMED_EXACT / OVERPAID / TIMEOUT / refund),
  // which is the only point where it's safe to let auto-sync take
  // over again — the backend has reached a decision.
  //
  // - 'verify'  : user clicked Confirm payment; hold on verify-generic
  //               regardless of intermediate UNDERPAID polling ticks.
  // - 'payment' : user clicked Back to payment from an UNDERPAID
  //               detected screen; hold on the QR view while they
  //               top up; UNDERPAID polling ticks must not bounce.
  // - null      : no override; updateUIFromSession drives view from
  //               session status as before.
  const viewIntentRef = useRef<'verify' | 'payment' | null>(null);
  
  // Match timer behavior from WalletAddressEntry (10 mins)
  const { timeLeft, formatTime } = useWalletAddressTimer(600);

  // Persist overpayment data to localStorage for GameRouter to pick up
  const persistOverpayment = useCallback((session: StakePaymentSession) => {
    if (session.status === 'OVERPAID' && (session.paidAmount ?? 0) > (session.expectedAmount ?? 0)) {
      try {
        localStorage.setItem(
          `overpayment_${tournamentId}`,
          JSON.stringify({
            expectedAmount: session.expectedAmount ?? 0,
            paidAmount: session.paidAmount ?? 0,
            // TB-RULE-NULLISH-ONLY: use ?? not || so 'SOL' is not masked
            currency: session.stakeCurrency?.shortName ?? 'POL',
          })
        );
      } catch { /* localStorage full — non-critical */ }
    }
  }, [tournamentId]);

  // Helper to update UI based on stake session status. Auto-sync from
  // polling/socket: respects user view-intent overrides for everything
  // except terminal statuses, where the backend has settled and we
  // always want to drive the user to the resolved view.
  const updateUIFromSession = useCallback((session: StakePaymentSession) => {
    const { status, paidAmount } = session;
    const intent = viewIntentRef.current;

    if (status === 'CONFIRMED_EXACT' || status === 'OVERPAID') {
      if (status === 'OVERPAID') {
        persistOverpayment(session);
      }
      setVerificationMode('success');
      setStep('game');
      viewIntentRef.current = null;
    } else if (status === 'TIMEOUT') {
      setVerificationMode('timeout');
      setStep('verification');
      viewIntentRef.current = null;
    } else if (status === 'UNDERPAID' || (status === 'PENDING' && paidAmount > 0)) {
      if (intent === 'payment') {
        // User explicitly returned to the QR view to top up — the
        // still-UNDERPAID polling tick must not bounce them back.
        return;
      }
      if (intent === 'verify') {
        // User clicked Confirm payment after topping up; hold them on
        // verify-generic until the backend matches the new deposit
        // and we hit the terminal branch above.
        setVerificationMode('generic');
        setStep('verification');
        return;
      }
      setVerificationMode('underpaid');
      setStep('verification');
    } else if (status === 'PENDING' && paidAmount === 0) {
      if (intent === 'verify') {
        setVerificationMode('generic');
        setStep('verification');
      } else {
        setVerificationMode('generic');
        setStep('payment');
      }
    } else {
      // REFUND_INITIATED, REFUNDED, REFUND_FAILED - show verification with generic message
      setVerificationMode('generic');
      setStep('verification');
      viewIntentRef.current = null;
    }
  }, [persistOverpayment]);

  // Polling fallback: callback updates session state same as socket handler.
  // Mirrors the socket handler's defensive merge — preserves the previous
  // `stakeCurrency` / `networkInfo` when the polling payload happens to
  // ship without chain context, so we never silently downgrade SOL → POL
  // through a partial response.
  const handlePollingUpdate = useCallback((session: StakePaymentSession) => {
    setStakeSession(prev => ({
      ...prev,
      ...session,
      depositAddress: prev?.depositAddress ?? session.depositAddress,
      stakeCurrency: session.stakeCurrency ?? prev?.stakeCurrency,
      networkInfo: session.networkInfo ?? prev?.networkInfo,
    }));
    updateUIFromSession(session);
  }, [updateUIFromSession]);

  const {
    isPolling: isStakePolling,
    startPolling: startStakePolling,
    stopPolling: stopStakePolling,
    fetchNow,
  } = useStakeSessionPolling({
    sessionId: stakeSession?.id ?? null,
    onSessionUpdate: handlePollingUpdate,
  });

  // Load stake payment session on mount
  useEffect(() => {
    const load = async () => {
      try {
        // 1. Create stake payment session
        const sessionResponse = await createStakePaymentSessionWithRetry(tournamentId);
        const session = sessionResponse.data;

        // 2. Handle already_confirmed — redirect instead of showing QR
        // Derive redirect from backend sessionType (authoritative), not transient location.state (F5)
        if (session.sessionState === 'already_confirmed') {
          persistOverpayment(session);
          const isJoiner = session.sessionType === 'JOIN';
          const redirectPath = isJoiner
            ? routes.GAME.replace(':id', String(tournamentId))
            : routes.TOURNAMENT_SHARE.replace(':id', String(tournamentId));
          navigate(redirectPath);
          return;
        }

        setStakeSession(session);

        // 3. Update UI based on session status
        updateUIFromSession(session);
      } catch (err) {
        console.error('Failed to initialize stake payment:', err);
        setError(err instanceof Error ? err : new Error('Failed to load payment session'));
      } finally {
        setIsLoading(false);
      }
    };

    if (tournamentId) {
      void load();
    } else {
      setIsLoading(false);
      setError(new Error('Missing tournament id'));
    }
  }, [tournamentId, updateUIFromSession, navigate]);

  // Socket listener for paymentSessionUpdated (source of truth)
  useEffect(() => {
    if (!token || !tournamentId) return;

    const connectSocket = async () => {
      try {
        await initializeSocket(token, undefined, () => {
          if (process.env.NODE_ENV !== 'production') {
            console.log('Socket connected for payment session updates');
          }
        });

        if (socket) {
          // Listen for payment session updates (source of truth)
          socket.on('paymentSessionUpdated', (data: PaymentSessionUpdatedPayload) => {
            const ps = data.paymentSession;
            // Check tournament match
            if (String(ps.tournament) === String(tournamentId)) {
              // Map snake_case from socket to camelCase. Chain context
              // (stake_currency / network_info) is shipped by current
              // backends but kept optional so older deployments still
              // work — when omitted, we preserve the previous values
              // from the spread merge below.
              const session: StakePaymentSession = {
                id: ps.id,
                status: ps.status,
                sessionType: ps.session_type,
                expectedAmount: ps.expected_amount,
                paidAmount: ps.paid_amount,
                paymentDeadline: ps.payment_deadline,
                paymentRequired: true,
                tournament: ps.tournament,
                stakeCurrency: ps.stake_currency
                  ? {
                      shortName: ps.stake_currency.short_name,
                      chain: ps.stake_currency.chain,
                      precision: ps.stake_currency.precision,
                    }
                  : undefined,
                networkInfo: ps.network_info
                  ? {
                      chain: ps.network_info.chain,
                      network: ps.network_info.network,
                      commitmentLevel: ps.network_info.commitment_level ?? null,
                    }
                  : undefined,
              };
              setStakeSession(prev => {
                // C: socket arrived before the initial REST hydration.
                // Trigger an immediate REST sync so the verify screen
                // gets the full chain context (deposit address, etc.)
                // without waiting for the next 5-second polling tick.
                if (!prev) {
                  void fetchNow();
                }
                return {
                  ...prev,
                  ...session,
                  depositAddress: prev?.depositAddress ?? session.depositAddress,
                  // Preserve hydrated chain context if the socket payload
                  // omitted it (legacy backends), but allow socket data
                  // to overwrite when both are present.
                  stakeCurrency: session.stakeCurrency ?? prev?.stakeCurrency,
                  networkInfo: session.networkInfo ?? prev?.networkInfo,
                };
              });
              updateUIFromSession(session);

              // Stop polling permanently on terminal statuses — keep fallback alive for PENDING/UNDERPAID
              if (['CONFIRMED_EXACT', 'OVERPAID', 'TIMEOUT', 'REFUND_INITIATED', 'REFUNDED', 'REFUND_FAILED'].includes(ps.status)) {
                stopStakePolling({ terminal: true });
              }
            }
          });

        }
      } catch (err) {
        console.error('Socket setup failed', err);
      }
    };

    connectSocket();

    return () => {
      if (socket) {
        socket.off('paymentSessionUpdated');
      }
    };
  }, [token, tournamentId, updateUIFromSession, stopStakePolling]);

  // Redirect after payment confirmed (backend auto-joins after payment)
  // Skip auto-redirect for OVERPAID with actual overpayment — user must click Continue
  const isOverpaid = stakeSession?.status === 'OVERPAID' && (stakeSession?.paidAmount ?? 0) > (stakeSession?.expectedAmount ?? 0);

  /**
   * Continue from the Detected/equal success screen.
   *
   * DS-2 specifies a manual Continue CTA on the Detected screen rather
   * than an auto-redirect timer. Backend already publishes the
   * tournament inside payment-session.handlePaymentConfirmed, so by the
   * time the user sees the success badge the tournament exists — this
   * callback only navigates them onward.
   */
  const handleEqualContinue = useCallback(() => {
    const isJoiner = stakeSession?.sessionType === 'JOIN';
    const path = isJoiner
      ? routes.GAME.replace(':id', String(tournamentId))
      : routes.TOURNAMENT_SHARE.replace(':id', String(tournamentId));
    navigate(path);
  }, [stakeSession?.sessionType, tournamentId, navigate]);

  const handleOverpaymentContinue = useCallback(() => {
    const isJoiner = stakeSession?.sessionType === 'JOIN';
    const path = isJoiner
      ? routes.GAME.replace(':id', String(tournamentId))
      : routes.TOURNAMENT_SHARE.replace(':id', String(tournamentId));
    navigate(path, {
      state: {
        overpayment: {
          expectedAmount: stakeSession?.expectedAmount ?? 0,
          paidAmount: stakeSession?.paidAmount ?? 0,
          currency: stakeSession?.stakeCurrency?.shortName ?? '',
        },
      },
    });
  }, [stakeSession?.sessionType, stakeSession?.expectedAmount, stakeSession?.paidAmount, stakeSession?.stakeCurrency?.shortName, tournamentId, navigate]);

  const t = getTranslations('en');

  const handleCancel = useCallback(() => {
    if (isJoinFlow) {
      const joinPath = hash
        ? `/tournaments/${tournamentId}/join?hash=${hash}`
        : `/join/${tournamentId}`;
      navigate(joinPath, { replace: true });
    } else {
      navigate(`/creating-tournament/${tournamentId}`, { replace: true });
    }
  }, [navigate, tournamentId, isJoinFlow, hash]);

  const handleConfirm = useCallback(() => {
     // Lock the view to verify until the backend reaches a terminal
     // status. This is the only way to ride out the inevitable window
     // where polling/socket still reports the previous UNDERPAID after
     // the user has topped up — without the lock, every 5 s polling
     // tick flips the screen back to the underpaid view for a few
     // seconds before OVERPAID/CONFIRMED arrives.
     viewIntentRef.current = 'verify';

     // Guard: no session yet — show verification as loading feedback
     if (!stakeSession) {
       setVerificationMode('generic');
       setStep('verification');
       return;
     }

     // If already confirmed via payment-session, redirect (handled by useEffect)
     if (['CONFIRMED_EXACT', 'OVERPAID'].includes(stakeSession.status)) {
        setVerificationMode('success');
        setStep('game');
        viewIntentRef.current = null;
        return;
     }

     // Switch to verification step to give visual feedback
     setVerificationMode('generic');
     setStep('verification');

     // Debounce: skip if polling already active
     if (isStakePolling) return;

     // Fallback: immediate status check + start 5s polling
     void fetchNow();
     startStakePolling();
  }, [stakeSession, isStakePolling, fetchNow, startStakePolling]);
  
  const handleCreateNewTournament = useCallback(() => {
    // For timeout - navigate back to tournament creation
    navigate('/creating-tournament', { replace: true });
  }, [navigate]);

  const handleBackToPayment = useCallback(() => {
    // Return to QR / address view without navigation. Lock the view
    // to payment so a still-UNDERPAID polling tick doesn't immediately
    // bounce the user back into the detected/under view while they're
    // topping up the missing amount. Cleared by the next handleConfirm
    // or whenever the backend hits a terminal status.
    viewIntentRef.current = 'payment';
    setVerificationMode('generic');
    setStep('payment');
  }, []);

  const handleCopyAddress = useCallback(async () => {
    if (!stakeSession?.depositAddress) return;
    const text = stakeSession.depositAddress;

    const markCopied = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    try {
      await navigator.clipboard.writeText(text);
      markCopied();
      return;
    } catch (err) {
      // navigator.clipboard.writeText rejects with "Document is not focused"
      // when the page lost focus (DevTools open, alt-tab, embedded preview).
      // Fall back to the legacy execCommand path — deprecated but still
      // ships in every major browser and bypasses the focus requirement.
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        if (document.execCommand('copy')) {
          markCopied();
          return;
        }
      } finally {
        document.body.removeChild(textarea);
      }
      console.error('Failed to copy', err);
    }
  }, [stakeSession?.depositAddress]);

  // Derive chain from session for branch decisions — no polygon default
  const sessionChain = stakeSession?.networkInfo?.chain ?? null;
  const isSolana = sessionChain === 'solana';

  // EVM chain IDs by chain name — extensible without hardcoding per-chain
  const EVM_CHAIN_IDS: Partial<Record<string, number>> = { polygon: 137 };

  const payment: Payment | null = stakeSession
    ? {
        id: String(stakeSession.id),
        status: 'PENDING' as PaymentStatus,
        type: 'TOURNAMENT_CREATE_ENTRY' as PaymentType,
        tournamentId: String(stakeSession.tournament ?? tournamentId),
        amount: String(stakeSession.expectedAmount ?? ''),
        currency: stakeSession.stakeCurrency?.shortName ?? '',
        provider: isSolana ? 'ONCHAIN_SOLANA' : 'ONCHAIN_POLYGON',
        expiresAt: '',
        providerData: sanitizeProviderData({
          toAddress: stakeSession.depositAddress ?? '',
          network: stakeSession.networkInfo?.network ?? '',
          chainId: EVM_CHAIN_IDS[sessionChain ?? ''] ?? null,
          tokenSymbol: stakeSession.stakeCurrency?.shortName ?? '',
          commitmentLevel: stakeSession.networkInfo?.commitmentLevel ?? null,
        }) as Payment['providerData'],
      }
    : null;

  // Use stakeSession as source of truth for payment amounts
  const isUnderpaid = stakeSession?.status === 'UNDERPAID' || 
    (stakeSession?.status === 'PENDING' && (stakeSession?.paidAmount ?? 0) > 0);
  const expectedAmount = stakeSession?.expectedAmount ?? 0;
  const paidAmount = stakeSession?.paidAmount ?? 0;
  const remainingAmount = Math.max(0, expectedAmount - paidAmount);
  const overpaymentAmount = Math.max(0, paidAmount - expectedAmount);
  const isTimeout = stakeSession?.status === 'TIMEOUT';

  return (
    <BaseWrapper compact>
      <GradientSection>
        <HeaderStack>
          <ProgressBar currentStep={step} />
        </HeaderStack>

        <Belt>
          <NetworkChip
            chain={
              (payment?.providerData?.network ?? '').toLowerCase().includes('solana')
                ? 'solana'
                : 'polygon'
            }
          />
          <TimerDisplay timeLeft={formatTime()} isWarning={timeLeft < 60} />
        </Belt>

        {(step === 'payment' || (step === 'verification' && (verificationMode === 'success' || verificationMode === 'underpaid'))) && (
          <FlowTag kind="to" />
        )}

        <Content>
          {isLoading && (
            <LoadingSkeleton data-testid="payment-qr-loading">
              <SkeletonLine $width="60%" $height="28px" />
              <SkeletonLine $width="80%" />
              <SkeletonLine $width="40%" />
              <SkeletonQR />
              <SkeletonLine $width="100%" />
              <SkeletonLine $width="70%" />
            </LoadingSkeleton>
          )}

          {error && !isLoading && (
            <ErrorBanner data-testid="wallet-assignment-error" role="alert">
              <span>⚠️</span>
              <span>{error.message}</span>
            </ErrorBanner>
          )}

          {!isLoading && !error && payment && (
            <>
              {step === 'payment' ? (
                <>
                  <PaymentTitle>
                    {isUnderpaid
                      ? renderAmountTitle(
                          'Please pay the remaining\n{amount} {currency} of the stake\nto join the tournament!',
                          remainingAmount.toFixed(2),
                          payment.currency || '',
                        )
                      : renderAmountTitle(
                          isJoinFlow ? t.instructions.titleJoin : t.instructions.title,
                          expectedAmount.toFixed(2),
                          payment.currency || '',
                        )}
                  </PaymentTitle>
                  
                  <PaymentSubtitle>
                    to this <strong>Trading Battles</strong> deposit address on{' '}
                    <em>{formatNetworkLabel(payment?.providerData?.network)}</em>.
                  </PaymentSubtitle>

                  <QRContainer data-testid="payment-instructions">
                    <QRFrame>
                      <QRArrowIn>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                          <line x1="17" y1="7" x2="7" y2="17" />
                          <polyline points="17 17 7 17 7 7" />
                        </svg>
                        {t.instructions.sendHere}
                      </QRArrowIn>
                      <QRCodeSVG
                        value={
                          payment.provider === 'ONCHAIN_SOLANA'
                            ? buildSolanaPayUrl({
                                recipient: payment.providerData.toAddress,
                                amount: payment.amount,
                                reference: String(stakeSession?.id ?? ''),
                              })
                            : payment.providerData.toAddress
                        }
                        size={188}
                        level="H"
                        bgColor="#52D381"
                        fgColor="#0A0A0A"
                        includeMargin={false}
                      />
                    </QRFrame>
                  </QRContainer>

                  <AddressSection>
                    <AddressRow>
                      <AddressLabel>{t.instructions.dedicatedAddress}</AddressLabel>{' '}
                      <AddressValue>
                        {addressRevealed
                          ? payment.providerData.toAddress
                          : truncateAddress(payment.providerData.toAddress)}
                      </AddressValue>
                    </AddressRow>
                    <AddressMeta>
                      {formatNetworkLabel(payment?.providerData?.network) || ''}
                      {' · send exactly '}
                      {expectedAmount.toFixed(2)} {payment.currency || ''}
                    </AddressMeta>
                    <AddressActions>
                      <RevealButton
                        type="button"
                        onClick={() => setAddressRevealed((v) => !v)}
                        aria-label={addressRevealed ? t.instructions.hide : t.instructions.revealFull}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        {addressRevealed ? t.instructions.hide : t.instructions.revealFull}
                      </RevealButton>
                      <CopyButton onClick={handleCopyAddress}>
                        {copied ? (
                          <>
                            <svg viewBox="0 0 16 16" fill="none" width={14} height={14}>
                              <path d="M3 8.5l3.2 3.2L13 5" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            {t.instructions.addressCopied}
                          </>
                        ) : (
                          <>
                            <img src={copyIconImg} alt="" width={14} height={14} />
                            {t.instructions.copyAddress}
                          </>
                        )}
                      </CopyButton>
                    </AddressActions>
                  </AddressSection>
                </>
              ) : (
                <VerificationContainer>
                  {verificationMode !== 'generic' && <VerificationHero />}
                  {verificationMode === 'underpaid' ? (
                    <>
                      <UnderpaidTitle>
                        Payment received, <span>but the amount is insufficient</span>
                      </UnderpaidTitle>

                      <OverpaymentDivider />

                      <UnderpaidDetails>
                        <UnderpaidRow>
                          <UnderpaidLabel>Expected:</UnderpaidLabel>
                          <UnderpaidValue>
                            {expectedAmount.toFixed(2)} {payment?.currency ?? 'POL'}
                          </UnderpaidValue>
                        </UnderpaidRow>
                        <UnderpaidRow>
                          <UnderpaidLabel>Received:</UnderpaidLabel>
                          <UnderpaidValue>
                            {paidAmount.toFixed(2)} {payment?.currency ?? 'POL'}
                          </UnderpaidValue>
                        </UnderpaidRow>
                      </UnderpaidDetails>

                      <UnderpaidRemaining>
                        Remaining stake to pay:{' '}
                        <span>
                          {remainingAmount.toFixed(2)} {payment?.currency ?? 'POL'}
                        </span>
                      </UnderpaidRemaining>

                      <UnderpaidHint>
                        {isJoinFlow
                          ? 'Please pay the remaining stake to join the tournament!'
                          : 'Please pay the remaining stake to create the tournament!'}
                      </UnderpaidHint>
                    </>
                  ) : verificationMode === 'success' ? (
                    isOverpaid ? (
                    <>
                      <SuccessIcon>
                        <CheckGlyph />
                      </SuccessIcon>

                      <VerificationTitle>
                        {isJoinFlow
                          ? <>You have <span>joined</span> the tournament!</>
                          : <>Tournament created <span>successfully!</span></>}
                      </VerificationTitle>

                      <VerificationSubText>
                        The tournament link has been also sent to your email (if you provided one)
                      </VerificationSubText>

                      <OverpaymentDivider />

                      <OverpaymentMessage>
                        Your transaction was successful, but you sent <b>more than the required stake</b>
                      </OverpaymentMessage>

                      <OverpaymentRow>
                        <OverpaymentLabel>Expected:</OverpaymentLabel>
                        <OverpaymentValue>
                          {expectedAmount.toFixed(2)} {payment.currency ?? 'POL'}
                        </OverpaymentValue>
                      </OverpaymentRow>
                      <OverpaymentRow>
                        <OverpaymentLabel>Received:</OverpaymentLabel>
                        <OverpaymentValue>
                          {paidAmount.toFixed(2)} {payment.currency ?? 'POL'}
                        </OverpaymentValue>
                      </OverpaymentRow>

                      <OverpaymentAmountLabel>Overpayment:</OverpaymentAmountLabel>
                      <OverpaymentAmountValue>
                        {overpaymentAmount.toFixed(2)} {payment.currency ?? 'POL'}
                      </OverpaymentAmountValue>

                      <OverpaymentHint>
                        Excess funds will be refunded after the tournament if they cover the transaction fee
                      </OverpaymentHint>

                      <DetectedContinueWrapper>
                        <DetectedContinueButton type="button" onClick={handleOverpaymentContinue}>
                          Continue
                        </DetectedContinueButton>
                      </DetectedContinueWrapper>
                    </>
                    ) : (
                    <>
                      <SuccessIcon>
                        <CheckGlyph />
                      </SuccessIcon>

                      <VerificationTitle>
                        {isJoinFlow
                          ? <>You have <span>joined</span> the tournament!</>
                          : <>Tournament created <span>successfully!</span></>}
                      </VerificationTitle>

                      <VerificationSubText>
                        The tournament link has been also sent to your email (if you provided one)
                      </VerificationSubText>

                      <OverpaymentDivider />

                      <DetectedContinueWrapper>
                        <DetectedContinueButton type="button" onClick={handleEqualContinue}>
                          Continue
                        </DetectedContinueButton>
                      </DetectedContinueWrapper>
                    </>
                    )
                  ) : verificationMode === 'timeout' ? (
                    <>
                      <VerificationTitle>
                        Payment session expired
                      </VerificationTitle>

                      <VerificationSubText>
                        Your payment session has timed out.
                        {'\n'}
                        {isJoinFlow
                          ? 'Please try joining the tournament again.'
                          : 'Please create a new tournament to try again.'}
                      </VerificationSubText>

                      <div style={{ height: 24 }} />

                      <PrimaryButton type="button" onClick={isJoinFlow ? handleCancel : handleCreateNewTournament}>
                        {isJoinFlow ? 'Try joining again' : 'Create new tournament'}
                      </PrimaryButton>
                    </>
                  ) : (
                    (() => {
                      // Mirror the NetworkChip pill's resolution: prefer
                      // providerData.network, then stakeSession.networkInfo,
                      // and finally default to 'polygon' so the verify copy
                      // never renders a blank network placeholder. The chain
                      // key drives both the ticker and the network label
                      // fallbacks below, keeping all three in sync.
                      const rawNetwork =
                        payment?.providerData?.network
                          || stakeSession?.networkInfo?.network
                          || stakeSession?.networkInfo?.chain
                          || '';
                      const isSolanaChain = rawNetwork.toLowerCase().includes('solana')
                        || rawNetwork.toLowerCase() === 'devnet'
                        || rawNetwork.toLowerCase() === 'testnet';
                      const fallbackTicker = isSolanaChain ? 'SOL' : 'POL';

                      const displayCurrency =
                        (payment?.currency && payment.currency.trim()) ||
                        stakeSession?.stakeCurrency?.shortName ||
                        deriveTickerFromNetwork(rawNetwork) ||
                        fallbackTicker;

                      // Amount source of truth: stakeSession.expectedAmount,
                      // with a string-numeric fallback to the synthesized
                      // payment.amount so users still see a number when
                      // verify renders before stakeSession finishes hydrating.
                      const restAmount = payment?.amount ? Number(payment.amount) : NaN;
                      const numericAmount =
                        typeof expectedAmount === 'number' && expectedAmount > 0
                          ? expectedAmount
                          : Number.isFinite(restAmount) && restAmount > 0
                            ? restAmount
                            : 0;
                      const hasAmount = numericAmount > 0;

                      const networkLabel =
                        formatNetworkLabel(payment?.providerData?.network) ||
                        formatNetworkLabel(stakeSession?.networkInfo?.network) ||
                        formatNetworkLabel(stakeSession?.networkInfo?.chain) ||
                        (isSolanaChain ? 'SOLANA' : 'POLYGON');
                      const [titleBefore, titleAfter = ''] = t.verify.title.split('{amount} {currency}');

                      return (
                        <>
                          <VerifyOrb>
                            <VerifyOrbCore>
                              <ShieldGlyph />
                            </VerifyOrbCore>
                          </VerifyOrb>

                          <VerificationTitle>
                            {hasAmount ? (
                              renderAmountTitle(
                                t.verify.title,
                                numericAmount.toFixed(2),
                                displayCurrency,
                              )
                            ) : (
                              // Amount still hydrating — keep the skeleton
                              // but already commit to the resolved ticker
                              // so the title reads "Your payment of ▭▭▭ POL
                              // is being verified" instead of stranding the
                              // user with no chain context.
                              <>
                                {titleBefore}
                                <VerifyAmountSkeleton aria-label="loading amount" />
                                {' '}
                                <span>{displayCurrency}</span>
                                {titleAfter}
                              </>
                            )}
                          </VerificationTitle>

                          <VerificationSubText>
                            {isJoinFlow ? t.verify.subtitleJoin : t.verify.subtitleCreate}
                          </VerificationSubText>

                          <VerifyStatus>
                            <VerifyPulseDot />
                            <span>{renderTransactionOnNetwork(t.verify.transactionOn, networkLabel)}</span>
                          </VerifyStatus>
                        </>
                      );
                    })()
                  )}
                </VerificationContainer>
              )}

              {verificationMode !== 'success' && verificationMode !== 'timeout' && (
                <ActionButtons>
                  {step === 'payment' ? (
                    <>
                      <SecondaryButton type="button" onClick={handleCancel}>
                        {t.actions.cancel}
                      </SecondaryButton>
                      <PrimaryButton type="button" onClick={handleConfirm}>
                        {t.actions.confirmPayment}
                      </PrimaryButton>
                    </>
                  ) : (
                    <BackToPaymentButton type="button" onClick={handleBackToPayment}>
                      {t.verify.backToPayment}
                    </BackToPaymentButton>
                  )}
                </ActionButtons>
              )}
            </>
          )}
        </Content>
      </GradientSection>
    </BaseWrapper>
  );
};

export default PaymentQR;
